import JSZip from "jszip";
import { useNavigate } from "react-router-dom";
import { read } from "xlsx";
import { downloadBlob } from "../utils/downloadBlob";
import { useContext, useRef, useState } from "react";
import { DataContext } from "../contexts/DataContext";
import { processXlsx, processJsonSheet } from "../utils/processing";
import { sheetNames } from "../utils/sheetConstants";
import { RawData } from "../contexts/DataContext";

export type UploadError = {
    msg: string; // The message for the user
    error?: string; // The extended error message
    data?: string[]; // Another extended error message
    info?: boolean; // informational notice (not a failure): render without the error/support styling
};

const useUploadFile = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<UploadError | null>(null);
    const navigate = useNavigate();
    const { rawDataReceived, setDataUploaded } = useContext(DataContext);
    // Cancellation: each run gets an id we also hand the backend, so /api/cancel/{id} can kill the exact
    // model subprocess (reliable behind Cloudflare, unlike relying on the browser's fetch disconnect). The
    // AbortController just releases the pending upload fetch so the UI doesn't sit waiting on a killed run.
    const runIdRef = useRef<string | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    const reset = () => {
        setError(null);
        rawDataReceived(null);
    };

    const cancel = async () => {
        const rid = runIdRef.current;
        setLoading(false); // instant feedback; the fetch below is torn down and the backend job is killed
        abortRef.current?.abort();
        if (rid) {
            try {
                await fetch(`/api/cancel/${rid}`, { method: "POST" });
            } catch (e) {
                console.error("cancel request failed", e); // best-effort: the abort already freed the UI
            }
        }
    };

    const upload = async (data: FormData) => {
        // when uploading a file, keep track of the original name for the download, remove extention
        const fileName = (data.get("file") as File).name.replace(".xlsx", "");
        // Capture the detail mode from the FormData here — further down, `data` is reassigned to
        // res.json() (a plain object with no .get()), so reading it there throws "C.get is not a function".
        const summaryOnly = data.get("detail_mode") === "summary_only";
        // Tag this run so it can be cancelled. randomUUID needs a secure context; fall back for plain-http dev.
        const runId = crypto?.randomUUID?.() ?? `${performance.now()}-${Math.random().toString(16).slice(2)}`;
        runIdRef.current = runId;
        data.append("run_id", runId);
        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        setDataUploaded(false);
        try {
            const res = await fetch("/api/upload", {
                method: "POST",
                body: data,
                signal: controller.signal,
                // result-by-reference: backend answers with small JSON ({token, dashboard}) and
                // parks the zip server-side; the browser then downloads it NATIVELY (streamed to
                // disk) instead of pulling it through fetch->JSZip->SheetJS, which froze the tab
                // on large results. Old backends ignore the header and stream the zip (legacy path).
                headers: { "X-Result-Mode": "reference" },
            });

            const contentType = res.headers.get("content-type");
            if (res.status === 200 && contentType === "application/zip") {
                const ab = await res.arrayBuffer();

                const zip = await JSZip.loadAsync(ab);
                const result_pai = await zip.file("pai_output.xlsx")?.async("arraybuffer");

                if (result_pai !== undefined && result_pai?.byteLength !== 0) {
                    downloadBlob(
                        new Blob([result_pai], {
                            type: "application/vnd.ms-excel",
                        }),
                        `${fileName}-PAI-results.xlsx`,
                    );
                }

                // Prefer the backend-provided dashboard.json: complete row data regardless of
                // whether sheets spilled to CSV (parsing the workbook yields n/a for spilled
                // sheets). Fall back to workbook parsing for outputs from older backends.
                let dashData: RawData | null = null;
                const dashFile = zip.file("dashboard.json");
                if (dashFile) {
                    try {
                        const dj = JSON.parse(await dashFile.async("string"));
                        dashData = {
                            employment: processJsonSheet(dj.sheets?.[sheetNames.Employment]),
                            ghg: processJsonSheet(dj.sheets?.[sheetNames.GHG]),
                            valueAdded: processJsonSheet(dj.sheets?.[sheetNames.ValueAdded]),
                            general: processJsonSheet(dj.sheets?.[sheetNames.General]),
                            attribution: processJsonSheet(dj.sheets?.[sheetNames.Attribution]),
                        };
                    } catch (e) {
                        dashData = null; // malformed json -> legacy workbook path below
                    }
                }

                const result = await zip.file("regular_output.xlsx")?.async("arraybuffer");
                if (result !== undefined) {
                    downloadBlob(
                        new Blob([result], {
                            type: "application/vnd.ms-excel",
                        }),
                        `${fileName}-results.xlsx`,
                    );
                    const rawData = dashData ?? processXlsx(read(result, { type: "array" }));

                    if (!rawData) {
                        setError({
                            msg: "Failed to extract raw data from xlsx",
                            error: "The model ran successfully and results are available in downloads, but cannot be displayed in the web interface.",
                        });
                    } else {
                        rawDataReceived(rawData);
                    }
                }
                setLoading(false);
                setDataUploaded(true);
                navigate("/dashboard");
            } else if (contentType === "application/json") {
                const data = await res.json();
                if (data.result === "cancelled") {
                    // backend confirms a user cancel (499). The abort usually wins the race, but if this
                    // response lands first, treat it as a clean stop — not an error.
                    setLoading(false);
                    return;
                }
                if (res.status === 200 && data.result === "reference") {
                    // Download each deliverable individually. The two workbooks stay small even for
                    // huge portfolios (oversize detail sheets spill OUT to CSV), so fetch+saveBlob
                    // them (reliable multi-file download, correct filenames). The CSV bundle can be
                    // large -> native <a> stream to disk, never held in JS memory.
                    const files: string[] = data.files ?? [];
                    const hasDetail = files.includes("detail.zip");
                    for (const name of files) {
                        const url = `/api/download/${data.token}/${name}`;
                        if (name === "detail.zip") {
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `${fileName}-detail.zip`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                        } else {
                            try {
                                const r = await fetch(url);
                                if (r.ok) {
                                    downloadBlob(
                                        await r.blob(),
                                        name === "pai_output.xlsx"
                                            ? `${fileName}-PAI-results.xlsx`
                                            : `${fileName}-results.xlsx`,
                                    );
                                }
                            } catch (e) {
                                console.error("download failed", name, e);
                            }
                        }
                    }
                    let rawData: RawData | null = null;
                    if (data.dashboard?.sheets) {
                        const sh = data.dashboard.sheets;
                        rawData = {
                            employment: processJsonSheet(sh[sheetNames.Employment]),
                            ghg: processJsonSheet(sh[sheetNames.GHG]),
                            valueAdded: processJsonSheet(sh[sheetNames.ValueAdded]),
                            general: processJsonSheet(sh[sheetNames.General]),
                            attribution: processJsonSheet(sh[sheetNames.Attribution]),
                        };
                    }
                    if (rawData) {
                        rawDataReceived(rawData);
                        setDataUploaded(true);
                        navigate("/dashboard");
                    } else {
                        const nRows = Object.values(data.row_counts ?? {}).reduce(
                            (acc: number, v: any) => acc + (typeof v === "number" ? v : 0),
                            0,
                        );
                        setError({
                            info: true,
                            msg: "Results downloaded as files.",
                            error:
                                `The portfolio is too large for the interactive dashboard` +
                                (nRows ? ` (${nRows.toLocaleString()} result rows)` : "") +
                                `. The two workbooks downloaded as individual files` +
                                (hasDetail
                                    ? `, plus a detail.zip with the row-level sheets as CSV`
                                    : summaryOnly
                                      ? ` (Summary only — row-level detail omitted; re-run with a detail option for the full data)`
                                      : ``) +
                                `. The Table sheet works in Excel at any size.`,
                        });
                    }
                    setLoading(false);
                    return;
                }
                if (typeof data.msg === "string") {
                    setError(data as UploadError);
                } else {
                    setError({ msg: `Unexpected error - ${data[0].msg}`, error: data[0].error });
                }
            } else {
                setError({ msg: "Unexpected error - generic", error: `Status ${res.status} | Contenttype ${contentType}` });
            }
        } catch (e) {
            if ((e as any)?.name === "AbortError") {
                setLoading(false); // user cancelled the upload fetch — not an error
                return;
            }
            console.error(e);
            setError({ msg: "Unexpected error - exception", error: `Exception ${e}` });
        }
        setLoading(false);
    };
    return { upload, loading, error, reset, setError, cancel };
};

export default useUploadFile;
